<template>
    <v-card-title>{{ $t("sidebar.companyCheck") }} </v-card-title>

    <v-card-text>
        <div class="d-flex flex-row justify-content-center mb-5">
            <v-text-field
                v-model="searchQuery"
                :label="$t('common.search')"
                prepend-inner-icon="mdi-magnify"
                @keydown.enter="fetchCompanies"
                hide-details
                clearable
            />
            <v-btn
                color="primary"
                class="ml-2"
                :loading="loading"
                @click="fetchCompanies"
            >
                {{ $t("common.search") }}
            </v-btn>
        </div>
        <v-data-table
            :headers="translatedHeaders"
            :items="companies"
            :loading="loading"
            class="elevation-1"
            item-value="id"
            density="compact"
            :no-data-text="$t('company.noCompaniesFound')"
            :footer-props="{ itemsPerPageOptions: [10, 25, 50] }"
            @click:row="goToCompanyDetails"
            hover
            clickable
        />
    </v-card-text>
</template>

<script>
import axios from "axios";
import { showAlertModal } from "@/utils";

export default {
    name: "CompanySearch",
    data() {
        return {
            searchQuery: "",
            companies: [],
            loading: false,
            abortCtrl: null,
        };
    },
    computed: {
        translatedHeaders() {
            return [
                { title: this.$t("company.name"), key: "name" },
                {
                    title: this.$t("register.companyType.title"),
                    key: "type_display",
                },
                { title: this.$t("company.dateRegistered"), key: "created_at" },
            ];
        },
    },
    mounted() {
        this.fetchCompanies();
    },
    beforeUnmount() {
        /* Cancels any in-flight request */
        if (this.abortCtrl) this.abortCtrl.abort();
    },
    methods: {
        showAlertModal,
        goToCompanyDetails(event, company) {
            if (company?.item?.id) {
                this.$router.push({
                    name: "CompanyDetails",
                    params: { id: company.item.id },
                });
            }
        },
        formatDate(dateStr) {
            const date = new Date(dateStr);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            return `${day}.${month}.${year}`;
        },
        getTypeDisplay(type) {
            const typeMap = {
                0: this.$t("register.companyType.transportator"),
                1: this.$t("register.companyType.speditie"),
                2: this.$t("register.companyType.producator"),
            };
            return typeMap[type] || this.$t("company.unknownType");
        },
        async fetchCompanies() {
            // cancel any previous call
            this.abortCtrl?.abort();
            const controller = new AbortController();
            this.abortCtrl = controller;

            this.loading = true;
            try {
                const resp = await axios.get(
                    `${process.env.VUE_APP_API_URL}/companies/`,
                    {
                        params: { search: this.searchQuery },
                        signal: controller.signal,
                    }
                );

                const payload = resp?.data ?? resp;

                if (!payload) {
                    // 204 or empty interceptor return
                    this.companies = []; // show “no companies found”
                    return; // nothing else to do
                }

                if (!this.$el) return; // component already unmounted

                this.companies = payload.map((c) => ({
                    ...c,
                    type_display: this.getTypeDisplay(c.type),
                    created_at: this.formatDate(c.created_at),
                }));
            } catch (err) {
                const isCanceled =
                    axios.isCancel?.(err) ||
                    err?.code === "ERR_CANCELED" ||
                    err?.name === "CanceledError";

                if (!isCanceled) {
                    console.error("Error fetching companies:", err);
                    this.showAlertModal(
                        this.$store,
                        this.$t("company.fetchError"),
                        "danger",
                        12_000,
                        err
                    );
                }
            } finally {
                if (this.$el) this.loading = false;
            }
        },
    },
};
</script>
